import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Mic2, Users, Loader, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../data/images";
import CTABand from "../components/shared/CTABand";
import api from "../utils/api";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const PLACEHOLDER_SPEAKERS = Array.from({ length: 12 }, (_, i) => ({
  id: `speaker-${i + 1}`,
  name: "Speaker TBA",
  title: "AI Leader",
  company: "To Be Announced",
}));

function SpeakerCard({ speaker, index }) {
  return (
    <RevealSection delay={index * 0.04}>
      <div className="card card-glow overflow-hidden group" data-testid={`speaker-card-${speaker.id}`}>
        <div className="relative h-56 overflow-hidden">
          <img src={IMAGES.speakerPlaceholder} alt={speaker.name}
            className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500 group-hover:scale-105" 
            style={{ transition: "transform 0.5s ease, opacity 0.3s ease" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, rgba(9,13,22,0.9) 100%)" }} />
        </div>
        <div className="p-5">
          <h4 className="font-bold text-white mb-1" style={{ fontFamily: "var(--font-heading)" }}>{speaker.name}</h4>
          <div className="text-xs" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            {speaker.title} · {speaker.company}
          </div>
        </div>
      </div>
    </RevealSection>
  );
}

function ApplyToSpeakForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", title: "", session_title: "", session_description: "", track: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/speaker-apply", { ...form, bio: "" });
      setSuccess(true);
      toast.success("Application submitted!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12" data-testid="speaker-apply-success">
        <CheckCircle size={40} className="mx-auto mb-4" style={{ color: "var(--cyan)" }} />
        <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>Application Received.</h3>
        <p style={{ color: "var(--text-muted)" }}>We'll review your submission and be in touch within 5 business days.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="speaker-apply-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Full Name *</label>
          <input name="name" value={form.name} onChange={handleChange} required placeholder="Jane Smith" className="input-dark" data-testid="speaker-name-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Email *</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="jane@company.com" className="input-dark" data-testid="speaker-email-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Company</label>
          <input name="company" value={form.company} onChange={handleChange} placeholder="Company Name" className="input-dark" data-testid="speaker-company-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Job Title</label>
          <input name="title" value={form.title} onChange={handleChange} placeholder="Chief AI Officer" className="input-dark" data-testid="speaker-title-input" />
        </div>
      </div>
      <div>
        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Proposed Session Title *</label>
        <input name="session_title" value={form.session_title} onChange={handleChange} required placeholder="How We Deployed AI Agents Across 50 Stores" className="input-dark" data-testid="speaker-session-title-input" />
      </div>
      <div>
        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Session Description *</label>
        <textarea name="session_description" value={form.session_description} onChange={handleChange} required rows={4} placeholder="Describe your proposed session, key takeaways, and why it fits Core AI Summit..." className="input-dark resize-none" data-testid="speaker-description-input" />
      </div>
      <div>
        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Target Track</label>
        <select name="track" value={form.track} onChange={handleChange} className="input-dark" data-testid="speaker-track-select">
          <option value="">Select a track</option>
          <option value="business_smb">Business & SMB Adoption</option>
          <option value="enterprise">Enterprise & Leadership</option>
          <option value="technical">Technical & Builders</option>
          <option value="any">Any / Let the team decide</option>
        </select>
      </div>
      <button type="submit" disabled={loading} className="btn-primary" data-testid="speaker-apply-submit">
        {loading ? <><Loader size={14} className="animate-spin" /> Submitting...</> : <>Submit Application <ArrowRight size={14} /></>}
      </button>
    </form>
  );
}

export default function SpeakersPage() {
  return (
    <div data-testid="speakers-page">
      {/* Hero */}
      <section className="page-hero" style={{ backgroundColor: "var(--surface)" }}>
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">Core AI Summit 2027</div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-6"
              style={{ fontFamily: "var(--font-heading)" }}>Speaker Lineup.<br />Coming Soon.</h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              We're building a speaker program unlike any Canadian AI event before it — practitioners, not talking heads. Real case studies, not polished pitches.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Speaker Grid */}
      <section className="section-py" data-testid="speakers-grid-section">
        <div className="container-xl">
          <RevealSection>
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="overline-label mb-3">Speaker Announcements</div>
                <h2 className="text-3xl font-black tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>
                  100+ Speakers · Announcements Begin Soon
                </h2>
              </div>
              <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-xs"
                style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.2)", color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] animate-pulse" />
                Updating Weekly
              </div>
            </div>
          </RevealSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {PLACEHOLDER_SPEAKERS.map((speaker, i) => (
              <SpeakerCard key={speaker.id} speaker={speaker} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Apply to Speak */}
      <section id="apply" className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="apply-to-speak-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <RevealSection>
              <div>
                <div className="overline-label mb-4">Apply to Speak</div>
                <h2 className="text-4xl font-black tracking-tight text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  Share what you've built.
                </h2>
                <p className="text-base leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
                  Core AI Summit prioritizes practitioners — people who have actually implemented AI and can share real outcomes. If you have a genuine story to tell, we want to hear it.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Mic2, label: "Practitioner-first", desc: "Real implementation stories over vendor pitches" },
                    { icon: Users, label: "All tracks open", desc: "Business, Enterprise, and Technical tracks" },
                    { icon: CheckCircle, label: "Editorial review", desc: "All applications reviewed by our program team" },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label} className="flex items-start gap-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.15)" }}>
                          <Icon size={16} style={{ color: "var(--cyan)" }} />
                        </div>
                        <div>
                          <div className="font-semibold text-white text-sm mb-0.5">{item.label}</div>
                          <div className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </RevealSection>

            <RevealSection delay={0.15}>
              <div className="p-8 rounded-2xl" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                <h3 className="text-xl font-black text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  Speaker Application
                </h3>
                <ApplyToSpeakForm />
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      <CTABand
        title="Want to attend instead?"
        subtitle="Join the priority list to be first to know when tickets open."
        primaryLabel="Join Priority List"
      />
    </div>
  );
}
